import CircleIcon from 'client/pages/MapPage/components/Legend/LegendIcons/CircleIcon/CircleIcon';
import ImageIcon from 'client/pages/MapPage/components/Legend/LegendIcons/ImageIcon/ImageIcon';
import LineIcon from 'client/pages/MapPage/components/Legend/LegendIcons/LineIcon/LineIcon';
import PolygonIcon from 'client/pages/MapPage/components/Legend/LegendIcons/PolygonIcon/PolygonIcon';
import SquareIcon from 'client/pages/MapPage/components/Legend/LegendIcons/SquareIcon/SquareIcon';
import TriangleIcon from 'client/pages/MapPage/components/Legend/LegendIcons/TriangleIcon/TriangleIcon';

import { iconsBaseUrl } from 'client/utils/constants/frontEndConfig';
import GeometryType from 'client/utils/constants/geometryType';
import SymbolName from 'client/utils/constants/symbolName';
import { evaluateStyleRules } from 'client/utils/evaluators/styleRuleEvaluator';

const { POINT, POLYGON, LINE_STRING } = GeometryType;

export const getRulesToDisplay = (assetType, sectorSpecificStyles) => {
    const { enabled, sector } = sectorSpecificStyles;

    if (!enabled) {
        return 'default';
    }

    if (!sector) {
        return 'isolated_views';
    }

    return assetType?.startsWith(sector) ? 'isolated_views' : 'default';
};

export const getStyleRuleOrder = styleRule => {
    if (Number.isFinite(styleRule.legendDisplayOrder)) {
        return styleRule.legendDisplayOrder;
    }

    if (Number.isFinite(styleRule.priority)) {
        return styleRule.priority;
    }

    return Number.MAX_SAFE_INTEGER;
};

export const sortStyleRulesByDisplayOrder = styleRules =>
    [...styleRules].sort((a, b) => getStyleRuleOrder(a) - getStyleRuleOrder(b));

export const getDisplayableStyleRulesForLayer = (styleRules, layerAssets, assetType, sectorSpecificStyles) => {
    if (!Array.isArray(styleRules) || styleRules.length === 0) {
        return [];
    }

    const rulesToDisplay = getRulesToDisplay(assetType, sectorSpecificStyles);

    return sortStyleRulesByDisplayOrder(
        styleRules.filter(styleRule => styleRule.mapMode === rulesToDisplay)
    ).map(rule => ({ rule, layerAssets }));
};

export const getDisplayableLegendItemsForCategory = ({
    categoryNode,
    currentLocale,
    mapLayers,
    assets,
    toggledLayers,
    dynamicLegend,
    sectorSpecificStyles,
    mapStyles
}) => {
    if (!categoryNode || !Array.isArray(mapLayers) || !Array.isArray(mapStyles)) {
        return [];
    }

    const stylesVisited = [];

    const hasAssets = layerName => {
        const features = assets[layerName];
        return features && features.length > 0;
    };

    const orderedRules = categoryNode.leaves()
        .filter(layerNode => !dynamicLegend || (hasAssets(layerNode.data.assetType) && toggledLayers.includes(layerNode.data.assetType)))
        .reduce((allRules, layerNode) => {
            const layerAssets = assets[layerNode.data.assetType];
            const layer = mapLayers.find(currentLayer => currentLayer.id === layerNode.id);
            const rulesForLayer = getDisplayableStyleRulesForLayer(
                layer?.styleRules,
                layerAssets,
                layerNode.data.assetType,
                sectorSpecificStyles
            );

            if (rulesForLayer.length === 0) {
                return allRules;
            }

            return [...allRules, ...rulesForLayer];
        }, [])
        .sort((a, b) => getStyleRuleOrder(a.rule) - getStyleRuleOrder(b.rule));

    return orderedRules.reduce((legendItems, { rule, layerAssets }) => {
        const style = mapStyles.find(currentStyle => currentStyle.id === rule.styleId);
        const passedStyleRule = !dynamicLegend || layerAssets.some(asset => evaluateStyleRules(asset, [rule], sectorSpecificStyles));

        if (!style || stylesVisited.includes(style.id) || !passedStyleRule) {
            return legendItems;
        }

        stylesVisited.push(style.id);

        return [
            ...legendItems,
            {
                id: rule.id,
                icon: styleRuleToIcon(style),
                label: rule.displayName[currentLocale]
            }
        ];
    }, []);
};

export const styleRuleToIcon = (style) => {
    if (style.geometryType === POLYGON) {
        return <PolygonIcon
            fill={style.fill}
            stroke={style.stroke}
            imageSource={style.imageSource ? new URL(style.imageSource, iconsBaseUrl) : null}
        />;
    } else if (style.geometryType === LINE_STRING) {
        return <LineIcon {...style.stroke} />;
    } else if (style.geometryType === POINT && style.symbolName === SymbolName.CIRCLE) {
        return <CircleIcon
            radius={style.image ? style.image.radius : style.radius}
            fill={style.image ? style.image.fill : style.fill}
            stroke={style.image ? style.image.stroke : style.stroke}
            displacement={style.image ? style.image.displacement : style.displacement}
        />;
    } else if (style.geometryType === POINT && style.symbolName === SymbolName.SQUARE) {
        return <SquareIcon
            fill={style.image ? style.image.fill : style.fill}
            stroke={style.image ? style.image.stroke : style.stroke}
        />;
    } else if (style.geometryType === POINT && style.symbolName === SymbolName.TRIANGLE) {
        return <TriangleIcon
            fill={style.image ? style.image.fill : style.fill}
            stroke={style.image ? style.image.stroke : style.stroke}
        />;
    } else if (style.geometryType === POINT && style.symbolName === SymbolName.ICON && style.image) {
        return <ImageIcon imageSource={new URL(style.image.src, iconsBaseUrl)} />;
    }

    return null;
};
